'use client';

import { useLocale, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import type { Value } from 'react-calendar/dist/esm/shared/types.js';

import { programDayKey, programDayKeyFromToday, todayProgramDayKey } from '@/helpers/program-day';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import { IconCalender24 } from '@cineamo/ui/src/icons/ic-calendar';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useUserAgent } from '@/providers/UserAgentProvider';
import { formatDate_yyyyMMdd } from '@/utils/date-helper';

type DatePickerProps = {
    date: string | 'all';
    onChange: (date: string) => void;
};

const DatePicker: React.FC<DatePickerProps> = ({ date, onChange }) => {
    const { timeZone } = useDomainConfiguration();
    const { isMobile: userAgentIsMobile } = useUserAgent();
    // `useIsMobile` reads the media query synchronously, so without the mount gate the native date
    // input existed in the first client render but not in the server HTML — a tree mismatch that
    // made React discard the whole program page's server markup (CIN-14426).
    const isMounted = useIsMounted();
    const isViewportMobile = useIsMobile();
    const isMobile = isMounted && isViewportMobile && userAgentIsMobile;
    const t = useTranslations();

    const [isOpen, setIsOpen] = useState(false);
    const mobileDateInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    // Custom logic for handling clicks outside the element
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen) {
                const target = event.target as Node;

                // Check if the click was on the calendar button
                if (buttonRef.current && buttonRef.current.contains(target)) {
                    return; // Don't close popup if click was on the button
                }

                // Check if the click was outside the popup
                if (popupRef.current && !popupRef.current.contains(target)) {
                    setIsOpen(false);
                }
            }
        };

        if (typeof document !== 'undefined') {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            if (typeof document !== 'undefined') {
                document.removeEventListener('mousedown', handleClickOutside);
            }
        };
    }, [isOpen]);

    const handleInputDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        onChange(value);
    };

    const handleDateChange = (selectedDate: Value) => {
        if (!(selectedDate instanceof Date)) {
            return;
        }
        onChange(formatDate_yyyyMMdd(selectedDate));
        setIsOpen(false);
    };

    const currentDate = useMemo(() => {
        if (date === 'all') {
            return todayProgramDayKey(timeZone);
        }
        return date;
    }, [date, timeZone]);

    // Compared as day keys rather than timestamps: both sides already name a calendar day on the
    // cinema's clock, and `YYYY-MM-DD` orders lexicographically exactly as the days do.
    const isCalendarSelected = useMemo(
        () => date !== 'all' && programDayKey(date, timeZone) >= programDayKeyFromToday(14, timeZone),
        [date, timeZone]
    );

    const onCalendarIconClick = useCallback(() => {
        if (!isMobile) {
            setIsOpen(!isOpen);
        }
        // For mobile: The input element is positioned on top of the button,
        // so clicks go directly to the native input (iOS requires direct user interaction)
    }, [isMobile, isOpen]);

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                className={cn(
                    'w-[56px] h-[56px] p-[16px] rounded-[4px] flex items-center justify-center cursor-pointer transition-all duration-150 hover:opacity-80',
                    isCalendarSelected
                        ? 'bg-[var(--primary-color)] text-[var(--on-primary-color)]'
                        : 'bg-[var(--surface-color)] text-[var(--on-surface-color)]'
                )}
                onClick={onCalendarIconClick}
                aria-label={t('program.open-calendar') || 'Open calendar'}>
                <IconCalender24 />
            </button>
            {isMobile && (
                <input
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    type="date"
                    id="date-picker-mobile"
                    name="date-picker"
                    value={currentDate}
                    min={todayProgramDayKey(timeZone)}
                    ref={mobileDateInputRef}
                    onChange={handleInputDateChange}
                />
            )}
            {isOpen && (
                <div
                    ref={popupRef}
                    className="absolute top-full right-0 mt-2 z-50 rounded-[13px] shadow-lg">
                    <CalendarComponent
                        selectedDate={currentDate}
                        onDateChange={handleDateChange}
                    />
                </div>
            )}
        </div>
    );
};

function CalendarComponent(props: { selectedDate?: string; onDateChange?: (date: Date) => void }) {
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        props.selectedDate ? new Date(props.selectedDate) : null
    );

    const locale = useLocale();

    const handleDateChange = (date: Value) => {
        if (!(date instanceof Date)) {
            return;
        }
        setSelectedDate(date);
        props.onDateChange?.(date);
    };

    const calendarStyles = {
        '--calendar-bg': 'var(--non-transparent-surface-color)',
        '--calendar-text': 'var(--on-surface-color)',
        '--calendar-primary': 'var(--primary-color)',
        '--calendar-primary-text': 'var(--on-primary-color)',
        '--calendar-secondary': 'var(--secondary-color)'
    } as React.CSSProperties;

    return (
        <div
            className="showtimes-calendar-wrapper"
            style={calendarStyles}>
            <Calendar
                locale={locale}
                className="showtimes-calendar"
                onChange={(value) => handleDateChange(value)}
                showNeighboringMonth={false}
                value={selectedDate}
                minDate={new Date()}
                minDetail="month"
                maxDetail="month"
            />
        </div>
    );
}

export default DatePicker;
